import React, { useCallback, useEffect, useState } from "react";
import { papi } from "../../../lib/portalApi";
import { T, track } from "./theme";
import { ScoreHero } from "./ScoreHero";
import { ScoreMainCard } from "./ScoreMainCard";
import { JourneyCard } from "./JourneyCard";
import { OpportunityDoors } from "./OpportunityDoors";
import { NextInstallmentCard } from "./NextInstallmentCard";
import { ReminderSheet } from "./ReminderSheet";
import { EducationSimulator } from "./EducationSimulator";
import { ExplainSheet, LimitSheet, BenefitsSheet } from "./Sheets";

const moodOf = (s, nd) => {
  if (nd?.overdue) return "late";
  if (nd) {
    const days = (new Date(`${nd.due_date}T12:00:00`) - new Date()) / 86400000;
    if (days <= 5) return "soon";
  }
  if (s?.journey?.on_time_count > 0) return "good";
  return "new";
};

const Skel = () => (
  <div className="space-y-4" data-testid="score-skeleton">{["h-80", "h-56", "h-48"].map((h) => <div key={h} className={`${h} animate-pulse rounded-[26px] bg-white/80`} />)}</div>
);

export default function ScoreScreen() {
  const [s, setS] = useState(null);
  const [ov, setOv] = useState(null);
  const [err, setErr] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [remind, setRemind] = useState(null);

  const load = useCallback(async () => {
    setErr(false);
    try {
      const [a, b] = await Promise.all([papi.get("/portal/score"), papi.get("/portal/overview")]);
      setS(a.data); setOv(b.data);
    } catch { setErr(true); }
  }, []);
  useEffect(() => { load(); track("score_view"); }, [load]);

  const openSheet = (k) => {
    setSheet(k);
    track({ explain: "score_explain_open", limit: "limit_open", benefits: "benefits_open" }[k]);
  };
  const onDoor = async (key) => {
    if (key === "limit") return openSheet("limit");
    if (key === "benefits") return openSheet("benefits");
    const win = window.open("", "_blank");
    try {
      const { data } = await papi.post("/portal/news-interest");
      const url = `https://wa.me/${data.whatsapp}?text=${encodeURIComponent("Olá! Quero conhecer as novidades da Cris Lingerie.")}`;
      if (win) win.location.href = url; else window.location.href = url;
    } catch { if (win) win.close(); }
  };

  const nd = ov?.next_due || null;
  return (
    <div className="min-h-full" style={{ background: "#F6F2FB" }}>
      <ScoreHero mood={s ? moodOf(s, nd) : "new"} />
      <div className="relative z-10 mx-auto -mt-20 w-full max-w-2xl space-y-4 px-4 pb-6 sm:px-5" data-testid="portal-score-page">
        {!s && !err && <Skel />}
        {err && (
          <div className="rounded-[22px] bg-white p-5 text-[15px]" data-testid="score-load-error">
            <span style={{ color: "#D32F45" }}>Não foi possível carregar seu score.</span>{" "}
            <button onClick={load} className="font-bold underline" style={{ color: T.violet }} data-testid="score-retry-btn">Tentar novamente</button>
          </div>
        )}
        {s && !s.available && (
          <div className="rounded-[26px] bg-white p-6 text-center" data-testid="score-unavailable">
            <b className="block text-[17px]" style={{ color: T.deep }}>Seu score está em construção</b>
            <span className="text-[14px]" style={{ color: T.muted }}>Ele aparece após as primeiras compras e pagamentos registrados.</span>
          </div>
        )}
        {s?.available && <ScoreMainCard s={s} onExplain={() => openSheet("explain")} />}
        {s && <div ref={(el) => { if (el && !el.dataset.seen) { el.dataset.seen = "1"; track("journey_view"); } }}><JourneyCard j={s.journey} /></div>}
        {s && <div ref={(el) => { if (el && !el.dataset.seen) { el.dataset.seen = "1"; track("opportunities_view"); } }}><OpportunityDoors items={s.opportunities} onAction={onDoor} /></div>}
        {s && ov && <NextInstallmentCard nextDue={nd} onRemind={(n, it) => setRemind({ nextDue: n, it })} onPaid={load} />}
        {s && <EducationSimulator />}
        {s && <p className="px-2 text-center text-[12px]" style={{ color: T.muted }} data-testid="portal-score-disclaimer">Avaliação interna. Não garante aprovação de crédito, limite ou benefícios.</p>}
      </div>
      {s && <ExplainSheet open={sheet === "explain"} onClose={() => setSheet(null)} s={s} />}
      {s && <LimitSheet open={sheet === "limit"} onClose={() => setSheet(null)} door={s.opportunities.find((o) => o.key === "limit")} />}
      {s && <BenefitsSheet open={sheet === "benefits"} onClose={() => setSheet(null)} />}
      {s && <ReminderSheet ctx={remind} reminder={s.reminder} onClose={() => setRemind(null)} onChanged={(r) => setS((x) => ({ ...x, reminder: r }))} />}
    </div>
  );
}
